"use client";

import React, { useMemo } from "react";
import { Heading, ImageWrapper, Text } from "ui-components";

import { ClientsSuccessStories } from "@/components/homepage/ClientsSuccessStories";
import { OurPartnerOrganizations } from "@/components/homepage/OurPartnerOrganizations";
import { KnowledgeGraphJsonLd } from "@/components/Schema/KnowledgeGraphJsonLd";
import { MobileAwareSlider } from "@/components/slider/MobileAwareSlider";
import { useOrganizationGetOrganizationMetaData } from "@/hooks/api/generated";
import MembershipsPage from "@/src/app/[locale]/(public)/about-us/[tab]/components/MembershipsPage";
import { memberships } from "@/src/app/[locale]/(public)/about-us/constants/memberships";
import CorporateSolutionsLeadForm from "@/src/components/organization/CorporateSolutionsLeadForm";
import { MEMBERSHIP_SLIDER_SETTINGS } from "@/src/constants/MembershipSlider";
import * as schemaGens from "@/utils/schema-generators";
import {
  getBreadcrumbSchema,
  getPageSchema,
  getPlatformSchema,
  getWebSiteSchema,
} from "@/utils/schema-generators";

const assetUrl =
  "https://res.cloudinary.com/dv6hrcluu/image/upload/corporate-solutions";

export default function CorporateSolutionsPage(): JSX.Element {
  const [allExpanded, setAllExpanded] = React.useState(false);
  const membershipItems = memberships.map((membership, index) => (
    <MembershipsPage
      key={`membership-${index.toString()}`}
      membership={membership}
      isExpanded={allExpanded}
      onToggle={() => setAllExpanded(!allExpanded)}
    />
  ));
  const { data: organisationMetaData } =
    useOrganizationGetOrganizationMetaData();

  const schemas = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let mentions: any = [];
    if (
      organisationMetaData &&
      organisationMetaData.featuredOrganizations?.length > 0
    ) {
      mentions = schemaGens.getMentions(
        organisationMetaData.featuredOrganizations
      );
    }
    return [
      getWebSiteSchema(true),
      getPlatformSchema({ includeBasicInfo: true }),
      getBreadcrumbSchema(
        [
          { name: "Home", url: "/" },
          { name: "About Us", url: "/about-us" },
          { name: "Corporate Solutions", url: "/about-us/corporate-solutions" },
        ],
        "/about-us/corporate-solutions/#breadcrumb"
      ),
      {
        ...getPageSchema({
          url: "/about-us/corporate-solutions/",
          name: "Corporate Solutions",
          type: "WebPage",
          description:
            "Explore our inclusive recruitment solutions and subscription tiers: Essentials, Bronze, Silver, Gold, and Platinum.",
        }),
        mainEntity: {
          "@id": "https://mygwork.com/about-us/corporate-solutions/#service",
        },
        about: {
          "@id": "https://mygwork.com/about-us/corporate-solutions/#service",
        },
      },
      {
        "@type": "Product",
        "@id": "https://mygwork.com/about-us/corporate-solutions/#product",
        name: "Inclusive Recruitment & Corporate Solutions",
        description:
          "Comprehensive subscription-based services helping organizations attract and retain LGBTQ+ talent.",
        brand: {
          "@id": "https://mygwork.com/#platform",
        },
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "GBP",
          lowPrice: "0.00",
          highPrice: "0.00",
          offerCount: "5",
          offers: [
            {
              "@type": "Offer",
              name: "Essentials Tier",
              description: "Suitable for small companies.",
              price: "0.00",
              priceCurrency: "GBP",
              priceType: "By Quote",
              availability: "https://schema.org/InStock",
              url: "https://mygwork.com/about-us/contact-us",
            },
            {
              "@type": "Offer",
              name: "Bronze Tier",
              description: "Suitable for growing teams.",
              price: "0.00",
              priceCurrency: "GBP",
              priceType: "By Quote",
              availability: "https://schema.org/InStock",
              url: "https://mygwork.com/about-us/contact-us",
            },
            {
              "@type": "Offer",
              name: "Silver Tier",
              description: "Suitable for mid-sized companies.",
              price: "0.00",
              priceCurrency: "GBP",
              priceType: "By Quote",
              availability: "https://schema.org/InStock",
              url: "https://mygwork.com/about-us/contact-us",
            },
            {
              "@type": "Offer",
              name: "Gold Tier",
              description: "Suitable for larger companies.",
              price: "0.00",
              priceCurrency: "GBP",
              priceType: "By Quote",
              availability: "https://schema.org/InStock",
              url: "https://mygwork.com/about-us/contact-us",
            },
            {
              "@type": "Offer",
              name: "Platinum Tier",
              description: "Suitable for enterprise organizations.",
              price: "0.00",
              priceCurrency: "GBP",
              priceType: "By Quote",
              availability: "https://schema.org/InStock",
              url: "https://mygwork.com/about-us/contact-us",
            },
          ],
        },
        mentions,
      },
    ];
  }, [organisationMetaData]);
  return (
    <>
      <div className="">
        <KnowledgeGraphJsonLd schemas={schemas} />
        <div className="flex flex-col md:px-0 px-5">
          <Heading align="center">Corporate Solutions</Heading>
          <CorporateSolutionsLeadForm
            intent="primary"
            triggerButtonSize="small"
            triggerButtonText="Enquire now"
            triggerButtonClasses="mx-auto w-60 mt-6 mb-8 md:mb-20"
          />
          <OurPartnerOrganizations page="corporate-solutions" />
          <Text
            align="center"
            className="mt-8 md:mt-20 !text-lg hidden md:block md:mb-10"
          >
            Discover our annual membership options.
          </Text>
          <Text align="center" className="text-[#535455] mt-10 md:hidden mb-4">
            ← Scroll to explore all plans →
          </Text>
        </div>
      </div>
      <div className="flex flex-col md:px-5 lg:px-0 px-0">
        <div className="w-full h-full justify-start items-start gap-[10px] hidden md:hidden lg:hidden xl:inline-flex 2xl:inline-flex">
          {membershipItems}
        </div>
        <div className="w-full xl:hidden 2xl:hidden">
          <MobileAwareSlider
            settings={MEMBERSHIP_SLIDER_SETTINGS}
            className="mx-2 lg:-mx-2.5"
            initialSlideOnMobile={3}
          >
            {membershipItems}
          </MobileAwareSlider>
        </div>
      </div>
      <div className="px-5 lg:px-0 mt-20">
        <ClientsSuccessStories />
      </div>
      <div className="px-5 lg:px-0">
        <div className="w-full h-full px-[60px] py-[48px] bg-[#FBE4D8] rounded-[20px] flex flex-col md:flex-row justify-center items-center gap-[24px] md:gap-[40px] mt-20">
          <div className="w-[180px] h-[156px] flex-shrink-0">
            <ImageWrapper
              src={`${assetUrl}/hire-top-talent-now_ungwpx.png`}
              width={160}
              height={145}
              alt="Hire top talent now"
              className="object-contain mx-auto"
            />
          </div>
          <div className="flex flex-col justify-start items-center md:items-start gap-[16px] md:gap-[24px] mt-4 md:mt-0">
            <div className="text-center md:text-left text-black text-[24px] md:text-[32px] font-redhat font-bold break-words">
              Hire top talent now
            </div>
            <div className="w-full flex justify-center md:justify-start">
              <CorporateSolutionsLeadForm
                intent="primary"
                triggerButtonSize="small"
                triggerButtonText="Enquire now"
                triggerButtonClasses="w-60"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
